<ng-container>
  <div *ngIf="isLoading" class="flex items-center justify-center h-screen">
    <mat-spinner color="primary"></mat-spinner>
  </div>
  <div *ngIf="!isLoading" class="flex flex-col w-full gap-6 mt-6">
    <!-- Message d'avertissement pour le mode édition de périmètre temporaire -->
    <vex-banner-list-message
      *ngIf="isEditingValidatedContractPerimeter"
      [title]="'Mode édition de périmètre d\'un contrat validé'"
      [errors]="temporaryPerimeterWarningMessages"
      [alertType]="'warn'"
    ></vex-banner-list-message>
    <div class="flex flex-row-reverse justify-between gap-2" *ngIf="entity">
      <span class="flex gap-2">
        <!-- Boutons pour le mode création (creationStep === 2) -->
        <button
          *ngIf="entity.creationStep === 2 && entity.perimeter.length > 0"
          color="primary"
          mat-raised-button
          type="button"
          (click)="updateStep(3)"
          [permissionBlock]="ENUM_PERMISSIONS.UPDATE_CONTRACT"
        >
          ENREGISTRER
        </button>

        <!-- Bouton MODIFIER pour les contrats non validés -->
        <button
          *ngIf="
            entity.creationStep !== 2 &&
            !(entity.status === ContractStatusEnum.VALIDATED) &&
            !(entity.status === ContractStatusEnum.EXPIRED)
          "
          color="primary"
          mat-raised-button
          type="button"
          (click)="switchToUpdateMode()"
          [permissionBlock]="ENUM_PERMISSIONS.UPDATE_CONTRACT"
        >
          MODIFIER
        </button>

        <!-- Nouveau workflow pour les contrats validés (uniquement isPredik) -->
        <!-- Bouton "Modifier le périmètre" pour activer le mode édition -->
        <button
          *ngIf="canEditValidatedContractPerimeter() && !isEditingValidatedContractPerimeter"
          color="primary"
          mat-raised-button
          type="button"
          (click)="enableValidatedContractPerimeterEdit()"
          [permissionBlock]="ENUM_PERMISSIONS.UPDATE_CONTRACT"
        >
          MODIFIER LE PÉRIMÈTRE
        </button>

        <!-- Boutons "Enregistrer" et "Annuler" en mode édition -->
        <button
          *ngIf="isEditingValidatedContractPerimeter"
          color="primary"
          mat-raised-button
          type="button"
          (click)="saveValidatedContractPerimeterEdit()"
          [permissionBlock]="ENUM_PERMISSIONS.UPDATE_CONTRACT"
        >
          ENREGISTRER
        </button>
        <button
          *ngIf="isEditingValidatedContractPerimeter"
          color="warn"
          mat-raised-button
          type="button"
          (click)="cancelValidatedContractPerimeterEdit()"
          [permissionBlock]="ENUM_PERMISSIONS.UPDATE_CONTRACT"
        >
          ANNULER
        </button>
      </span>
    </div>

    <div class="flex flex-row items-stretch justify-center gap-6 perimeter-columns-container">
      <!-- Section d'édition : affichée en mode création OU en mode édition de contrat validé -->
      <div
        *ngIf="entity.creationStep === 2 || isEditingValidatedContractPerimeter"
        class="w-2/4 card p-6 perimeter-column"
      >
        <div class="w-full">
          <button
            class="mb-4 w-full"
            mat-raised-button
            color="primary"
            (click)="updateCheckboxSelection(!isAllSelected())"
            [permissionBlock]="ENUM_PERMISSIONS.UPDATE_CONTRACT"
          >
            {{ selectAllButtonLabel }}
          </button>
        </div>

        <form *ngIf="!isReference" [formGroup]="searchForm" class="flex -mb-3">
          <div class="w-1/3 md:pr-2">
            <base-form-input
              [items]="removeDuplicatesAndSortByKey(itemsListNoDuplicate, 'genericId')"
              controlName="generics"
              displayProperty="genericLabel"
              label="Générique"
              disableAutoSelect="true"
              type="select"
            ></base-form-input>
          </div>

          <div class="w-1/3 md:pr-2">
            <base-form-input
              [items]="removeDuplicatesAndSortByKey(itemsListNoDuplicate, 'establishmentId')"
              controlName="establishments"
              displayProperty="establishmentLabel"
              label="Etablissement"
              disableAutoSelect="true"
              type="select"
            ></base-form-input>
          </div>
          <div class="w-1/3">
            <base-form-input
              [items]="removeDuplicatesAndSortByKey(itemsListNoDuplicate, 'buildingId')"
              controlName="buildings"
              displayProperty="buildingLabel"
              label="Bâtiment"
              disableAutoSelect="true"
              type="select"
            ></base-form-input>
          </div>
        </form>

        <div *ngIf="!isReference" class="w-full">
          <button class="mb-2 w-full" mat-raised-button color="primary" (click)="clearFilter()">Effacer filtres</button>
        </div>

        <div
          class="bg-card flex flew-row items-center rounded-full border mb-4 px-4 ltr:pr-4 rtl:pl-4 ltr:border-r rtl:border-l"
        >
          <ic-icon [icIcon]="icSearch" size="20px"></ic-icon>
          <input
            class="px-4 py-3 border-0 outline-none w-full bg-transparent"
            placeholder="Recherche..."
            [formControl]="searchControl"
            #search
          />
        </div>

        <!-- Conteneur pour superposer spinner et contenu -->
        <div class="loading-overlay-container perimeter-list-container">
          <!-- Spinner visible pendant 'loading' et 'transitioning' -->
          <div
            *ngIf="loadingState !== 'loaded'"
            [class.fading-out]="loadingState === 'transitioning'"
            class="spinner-container"
          >
            <mat-spinner diameter="50" color="primary"></mat-spinner>
          </div>

          <!-- Contenu visible pendant 'transitioning' et 'loaded' -->
          <form
            [class.fading-in]="loadingState === 'transitioning'"
            [class.visible]="loadingState === 'loaded'"
            [formGroup]="itemPerimeterForm"
            class="content-wrapper perimeter-list-scroll"
          >
            <div *ngFor="let item of itemsListNoDuplicate">
              <mat-checkbox
                [id]="item._id"
                [checked]="isSelected(item._id)"
                [disabled]="isNotAvailable(item._id) || isUpdateDisabledPermission"
                [value]="item._id"
                (change)="toggleSelection($event.checked, item._id)"
              >
                {{ item.label }}
                <span *ngIf="!isReference && item.buildingId === null" style="color: red">
                  {{ messageItemNotAvailable }}
                </span>
              </mat-checkbox>
            </div>
          </form>
        </div>
      </div>

      <!-- Section d'affichage du périmètre existant -->
      <div
        class="flex flex-col"
        [ngClass]="{
          'w-full': entity.creationStep !== 2 && !isEditingValidatedContractPerimeter,
          'w-2/4 perimeter-column': entity.creationStep === 2 || isEditingValidatedContractPerimeter
        }"
      >
        <vex-quote-perimeter-patrimony
          *ngIf="entity.quote && entity.quote.perimeter?.patrimony.length > 0"
          [compactMode]="true"
          [quote]="entity.quote"
          class="mb-3"
        ></vex-quote-perimeter-patrimony>

        <div class="card block pb-6 px-6 h-full perimeter-column-content">
          <!-- Titre permanent -->
          <div class="flex flex-row px-gutter border-b py-4 mb-6">
            <h2 class="title m-0">Eléments intégrés à l'engagement</h2>
          </div>

          <!-- Conteneur pour superposer spinner et contenu -->
          <div class="loading-overlay-container perimeter-list-container">
            <!-- Spinner visible pendant 'loading' et 'transitioning' -->
            <div
              *ngIf="loadingState !== 'loaded'"
              [class.fading-out]="loadingState === 'transitioning'"
              class="spinner-container"
            >
              <mat-spinner diameter="50" color="primary"></mat-spinner>
            </div>

            <!-- Contenu visible pendant 'transitioning' et 'loaded' -->
            <div
              [class.fading-in]="loadingState === 'transitioning'"
              [class.visible]="loadingState === 'loaded'"
              class="content-wrapper perimeter-list-scroll"
            >
              <vex-contract-details-user-perimeter
                [entity]="entity"
                (selectedItemsChange)="onSelectedItemsChange($event)"
                [readMode]="
                  (entity.creationStep !== 2 && !isEditingValidatedContractPerimeter) || isUpdateDisabledPermission
                "
                [isEditingTemporaryPerimeter]="isEditingValidatedContractPerimeter"
              ></vex-contract-details-user-perimeter>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Boutons d'ajout/retrait : affichés en mode création OU en mode édition de contrat validé -->
    <div
      class="flex flex-row items-stretch justify-center gap-6"
      *ngIf="entity.creationStep === 2 || isEditingValidatedContractPerimeter"
    >
      <button
        color="primary"
        *hasPermission="[ENUM_PERMISSIONS.UPDATE_CONTRACT]"
        mat-raised-button
        type="button"
        (click)="addItem()"
      >
        AJOUTER
        <mat-icon size="20" [icIcon]="faAngleRight"></mat-icon>
      </button>
      <button
        color="primary"
        *hasPermission="[ENUM_PERMISSIONS.UPDATE_CONTRACT]"
        mat-raised-button
        type="button"
        (click)="deleteItem()"
      >
        <mat-icon size="20" [icIcon]="faAngleLeft"></mat-icon>
        RETIRER
      </button>
    </div>
  </div>
</ng-container>
